/**
 * Global simulation constants.
 *
 * The simulation state is a *regular 2D grid* owned by the simulation engine.
 * The renderer only ever *reads* that state; nothing in the simulation layer
 * knows that a GPU or a mesh exists.
 *
 * ---------------------------------------------------------------------------
 * Units
 * ---------------------------------------------------------------------------
 *  - horizontal distance : metres (cell size is uniform, `cellSize` in config)
 *  - height              : metres above the grid's zero datum
 *  - water / sediment    : metres of equivalent thickness on a cell
 *  - time                : seconds (one `tick` advances `dtSim` seconds)
 *
 * The model is deliberately *accelerated*: a tick advances more simulated time
 * than a real frame does, and the erodibility constants are tuned so that
 * geologically slow processes become visible within seconds of wall-clock time.
 * See README "Time model".
 */

/** Number of f32 slots one cell occupies in every simulation buffer. */
export const STATE_STRIDE = 15;

/**
 * Field layout inside a state buffer. Every shader that packs state must use
 * this exact order (see `gpu/wgsl/lib.wgsl` `packState`).
 */
export const F = {
  /** Terrain surface elevation above datum [m]. */
  height: 0,
  /** Non-erodible bedrock elevation; `height >= bedrock` always [m]. */
  bedrock: 1,
  /** Mobile regolith thickness sitting on the bedrock [m]. */
  soil: 2,
  /** Water column thickness on the cell [m]. */
  water: 3,
  /** Suspended sediment concentration, as water-equivalent thickness [m]. */
  sediment: 4,
  /** Depth-averaged water velocity, +x component [m/s]. */
  velU: 5,
  /** Depth-averaged water velocity, +y component [m/s]. */
  velV: 6,
  /** Water flux across the cell boundary, +x component [m^3/(s*m)]. */
  fluxX: 7,
  /** Water flux across the cell boundary, +y component [m^3/(s*m)]. */
  fluxY: 8,
  /** Erosion positive / deposition negative accumulated this tick [m]. */
  erosion: 9,
  /** Deposition accumulated this tick (positive = material gained) [m]. */
  deposition: 10,
  /** Bedrock resistance to erosion, 0.1 (soft) .. 6 (very hard) [-]. */
  hardness: 11,
  /** Cumulative solid material carried beyond the domain by wind [m]. */
  windExport: 12,
  /** Multi-flow upstream contributing area, measured in cell equivalents. */
  flowAccum: 13,
  /** Water stored in the regolith and released gradually as baseflow [m]. */
  groundwater: 14,
} as const;

/** Bytes per cell in a state buffer (15 * 4). */
export const STATE_BYTES = STATE_STRIDE * 4;

/**
 * Bit mask written into the per-pass uniform. A pass reads one state buffer
 * (`cur`) and writes the other (`next`); every field *not* listed here is
 * forwarded verbatim so that a pass only ever pays for what it changes.
 */
export const FIELD_BIT = {
  height: 1 << 0,
  bedrock: 1 << 1,
  soil: 1 << 2,
  water: 1 << 3,
  sediment: 1 << 4,
  velU: 1 << 5,
  velV: 1 << 6,
  fluxX: 1 << 7,
  fluxY: 1 << 8,
  erosion: 1 << 9,
  deposition: 1 << 10,
  hardness: 1 << 11,
  windExport: 1 << 12,
  flowAccum: 1 << 13,
  groundwater: 1 << 14,
} as const;

/** All fields except the per-tick accumulators (which each pass overwrites). */
export const FIELD_ALL =
  FIELD_BIT.height |
  FIELD_BIT.bedrock |
  FIELD_BIT.soil |
  FIELD_BIT.water |
  FIELD_BIT.sediment |
  FIELD_BIT.velU |
  FIELD_BIT.velV |
  FIELD_BIT.fluxX |
  FIELD_BIT.fluxY |
  FIELD_BIT.hardness |
  FIELD_BIT.windExport |
  FIELD_BIT.flowAccum |
  FIELD_BIT.groundwater;

/** Grid resolutions offered in the UI. */
export const RESOLUTIONS = [128, 256, 512, 1024] as const;
export type Resolution = (typeof RESOLUTIONS)[number];

/** Time multipliers offered in the UI. */
export const TIME_SCALES = [0.25, 0.5, 1, 2, 4, 8, 16] as const;

/** Max mip levels used by the render ray-marching acceleration pyramid. */
export const MAX_MIP_LEVELS = 12;

/**
 * Numerical ceiling on the water column of a single cell [m].
 *
 * Deliberately far above anything physical. It exists only so that a genuinely
 * divergent run produces big numbers instead of `Infinity`/`NaN`, and it must be
 * high enough that normal operation never reaches it: clamping is a *mass
 * destroying* operation, so a ceiling that ordinary water columns can touch
 * silently drains the whole domain. An earlier value of 200 m did exactly that —
 * a lake that piled up against a valley wall lost 70% of its volume over a few
 * hundred ticks and looked like a solver bug.
 */
export const MAX_CELL_WATER = 1e6;
